import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { EntityBindingColumns } from "../metadata/BaseEntityRecordBinding";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { MirageConstants } from "../mirageConstants";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export default class DateFormatter {
    /**
     * Formats a date value to a string using the specified format.
     * @param value The date value to format.
     * @param format The format string to use. Defaults to "DD/MM/YYYY".
     * @returns The formatted date string.
     */
    static formatToDate(value: IDateValue, format = "DD/MM/YYYY"): string {
        const formattedDate = dayjs(value).format(format);
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatToDateOrNull(value: IDateValue, format = "DD/MM/YYYY"): string {
        if (!value) {
            return "null";
        }
        const formattedDate = dayjs(value).format(format);
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static parseToDate(value?: string, format: dayjs.OptionType = "DD/MM/YYYY") {
        if (!value) {
            return null;
        }
        return dayjs(value, format).toDate();
    }

    static formatToDateTime(value: IDateValue): string {
        const formattedDate = dayjs(value).format("DD/MM/YYYY HH:mm");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }

    /**
     * Formats the given date value to a string representation.
     * If the value is invalid or empty, an empty string is returned.
     *
     * @param value - The date value to format.
     * @returns The formatted date string. Defaults to "DD/MM/YYYY h:mm A"
     */
    static formatToDateTimeAmPm(value: IDateValue): string {
        const formattedDate = dayjs(value).format("DD/MM/YYYY h:mm A");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatToUtcDateTimeAmPm(value: IDateValue): string {
        const formattedDate = dayjs(value).utc().format("DD/MM/YYYY h:mm A");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }

    static formatToDateTimeWithSeconds(value: IDateValue, format: dayjs.OptionType = "DD/MM/YYYY"): string {
        const formattedDate = dayjs(value).format(`${format} HH:mm:ss`);
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatDateTimeToLocalTimeZoneAgnosticDateTimeString(date: Date): string {
        const dayjsDate = dayjs(date);
        const formattedString = dayjsDate.format("YYYY-MM-DDTHH:mm:ss");
        return formattedString;
    }

    static formatStringToLocalTimeZoneAgnosticDateTimeString(date: string): string {
        const timeZoneAgnosticDate = (date || "").replace("Z", "");
        const dayjsDate = dayjs(timeZoneAgnosticDate);
        const formattedString = dayjsDate.format("YYYY-MM-DDTHH:mm:ss");
        return formattedString;
    }

    static formatTimeAmPm(value: IDateValue): string {
        const formattedDate = dayjs(value).format("HH:mm A");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatTimeUtcAmPm(value: IDateValue): string {
        const formattedDate = dayjs(value).utc().format("HH:mm A");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatToISOStringDateTime(value: IDateValue): string {
        const formattedDate = dayjs(value).format("YYYY-MM-DDTHH:mm:ss.SSS");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }
    static formatToISOStringDate(value: IDateValue): string {
        const formattedDate = dayjs(value).format("YYYY-MM-DD");
        if (formattedDate === "Invalid Date" || !value) {
            return "";
        }
        return formattedDate;
    }

    static getUTCDate(value: IDateValue): string {
        return dayjs(value).utc(true).toISOString();
    }

    static formatPlainRecordDates(recordObj: any, colDefs: EntityBindingColumns<any>) {
        if (!recordObj) {
            return null;
        }
        const keys = Object.keys(recordObj);
        for (const key of keys) {
            const colDef = colDefs[key];
            if (!colDef) {
                continue;
            }
            if (colDef.contentType === "DATE") {
                if (recordObj[key]) {
                    recordObj[key] = DateFormatter.formatToDate(recordObj[key] as any);
                }
            }
            if (colDef.contentType === "DATETIME") {
                if (recordObj[key]) {
                    recordObj[key] = DateFormatter.formatToDateTime(recordObj[key] as any);
                }
            }
        }
        return recordObj;
    }

    static toTimezone(dayjsDate: Dayjs | Date | string, zone?: string, keepLocalTime?: boolean) {
        const format = "YYYY-MM-DDTHH:mm:ss";

        return dayjs(dayjsDate).tz(zone, keepLocalTime).format(format);
    }

    static get currentTimezone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    static formatRecordDates(recordObj: any, entityMap: Map<string, EntityBindingColumns<any>>, relEntity = "") {
        if (!recordObj) {
            return null;
        }
        const columns = entityMap.get(relEntity);
        if (!columns) {
            return recordObj;
        }
        const keys = Object.keys(recordObj);
        for (const key of keys) {
            const colDef = columns[key];
            if (!colDef) {
                continue;
            }
            if (colDef.contentType === "LOOKUP" && colDef.relatedObject && recordObj[key]) {
                const relatedRecordProp = key + MirageConstants.RelatedRecordSuffix;
                recordObj[relatedRecordProp] = this.formatRecordDates(recordObj[relatedRecordProp], entityMap, colDef.relatedObject);
            }
            if (colDef.contentType === "DATE") {
                if (recordObj[key]) {
                    recordObj[key] = DateFormatter.formatToDate(recordObj[key] as any);
                }
            }
            if (colDef.contentType === "DATETIME") {
                if (recordObj[key]) {
                    recordObj[key] = DateFormatter.formatToDateTime(recordObj[key] as any);
                }
            }
            if (colDef.contentType === "TIME") {
                if (recordObj[key]) {
                    recordObj[key] = DateFormatter.formatTimeAmPm(recordObj[key] as any);
                }
            }
        }
        return recordObj;
    }

    public static formatDatesInPayload(payload: any): any {
        if (payload instanceof Date) {
            return dayjs(payload).utc(true).format("YYYY-MM-DDTHH:mm:ss");
        }
        if (Array.isArray(payload)) {
            return payload.map((v) => this.formatDatesInPayload(v));
        }
        if (payload && typeof payload === "object") {
            return Object.keys(payload).reduce((acc: any, key) => {
                acc[key] = this.formatDatesInPayload(payload[key]);
                return acc;
            }, {});
        }
        return payload;
    }
}
export type IDateValue = string | number | Date | dayjs.Dayjs | null | undefined;
